import {
  createAsyncThunk,
  createSlice,
} from "@reduxjs/toolkit";

import { RootState } from "../../app/store";

import {
  geofenceList,
  getLandingZoneList,
  listLandingPads,
} from "./airspaceAPI";
import {
  CellGeofence,
  errorHasMannaMessage,
  errorHasMessage,
  Pad,
} from "../aircraft/types";

import { persistReducer } from "redux-persist";
import storage from "redux-persist/lib/storage";
import { LandingZone } from "@m4a3/api-client";

const TAG = "airspaceSlice";
export interface AirspaceState {
  pads: Pad[];
  geofences: CellGeofence[];
  landingZones: LandingZone[];
}

const initialState: AirspaceState = {
  pads: [],
  geofences: [],
  landingZones: [],
};

export const listLandingPadsThunk = createAsyncThunk<
  Pad[],
  {},
  {
    fulfilledMeta: { message?: string };
    rejectedMeta: { message?: string };
    state: RootState;
  }
>("airspace/listPads", async ({}, thunkApi) => {
  try {
    const res = await listLandingPads();
    return thunkApi.fulfillWithValue(res, {
      message: "[GET] /addressfind/listLandingPads succeeded!",
    });
  } catch (err) {
    const errorString = "[GET] /addressfind/listLandingPads failed";
    if (errorHasMannaMessage(err)) {
      return thunkApi.rejectWithValue(err, {
        message: `${errorString} with code ${err?.response?.data.errorCode}: ${err?.response?.data.errorMessage}`,
      });
    }
    if (errorHasMessage(err)) {
      return thunkApi.rejectWithValue(err.message, {
        message: `${errorString}: ` + err?.message,
      });
    }
    return thunkApi.rejectWithValue(err, {
      message: errorString,
    });
  }
});

export const getGeofenceListThunk = createAsyncThunk<
  CellGeofence[],
  {},
  {
    fulfilledMeta: { message?: string };
    rejectedMeta: { message?: string };
    state: RootState;
  }
>("airspace/getGeofenceList", async ({}, thunkApi) => {
  try {
    const res = await geofenceList();
    return thunkApi.fulfillWithValue(res, {
      message: "[GET] getGeofenceList succeeded!",
    });
  } catch (err) {
    const errorString = "[GET] getGeofenceList failed";
    if (errorHasMannaMessage(err)) {
      return thunkApi.rejectWithValue(err, {
        message: `${errorString} with code ${err?.response?.data.errorCode}: ${err?.response?.data.errorMessage}`,
      });
    }
    if (errorHasMessage(err)) {
      return thunkApi.rejectWithValue(err.message, {
        message: `${errorString}: ` + err?.message,
      });
    }
    return thunkApi.rejectWithValue(err, {
      message: errorString,
    });
  }
});

export const getLandingZoneListThunk = createAsyncThunk<
  LandingZone[],
  {},
  {
    fulfilledMeta: { message?: string };
    rejectedMeta: { message?: string };
    state: RootState;
  }
>("airspace/getLandingZoneList", async ({}, thunkApi) => {
  try {
    const cell = thunkApi.getState().session.cell;

    if (!cell) throw new Error("No cell selected");
    const res = await getLandingZoneList(cell.id);
    return thunkApi.fulfillWithValue(res, {
      message: "[GET] getLandingZoneList succeeded!",
    });
  } catch (err) {
    const errorString = "[GET] getLandingZoneList failed";
    if (errorHasMannaMessage(err)) {
      return thunkApi.rejectWithValue(err, {
        message: `${errorString} with code ${err?.response?.data.errorCode}: ${err?.response?.data.errorMessage}`,
      });
    }
    if (errorHasMessage(err)) {
      return thunkApi.rejectWithValue(err.message, {
        message: `${errorString}: ` + err?.message,
      });
    }
    return thunkApi.rejectWithValue(err, {
      message: errorString,
    });
  }
});

export const airspaceSlice = createSlice({
  name: "mission",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(listLandingPadsThunk.fulfilled, (state, action) => {
      state.pads = action.payload;
    });
    builder.addCase(getGeofenceListThunk.fulfilled, (state, action) => {
      state.geofences = action.payload;
    });
    builder.addCase(getLandingZoneListThunk.fulfilled, (state, action) => {
      state.landingZones = action.payload;
    });
  },
});

export const {} = airspaceSlice.actions;

export const selectMissionSelect = (state: RootState) =>
  state.mission.missionSelect;

export const selectPads = (state: RootState) => state.airspace.pads;
export const selectGeofences = (state: RootState) => state.airspace.geofences;
export const selectLandingZone = (state: RootState) =>
  state.airspace.landingZones;

const selectLatLon = (state: RootState, latLon: [number, number]) => latLon;

export default persistReducer(
  {
    key: "airspace",
    storage,
    whitelist: [],
  },
  airspaceSlice.reducer
);
